import { MAX_SCALE, MIN_SCALE } from './scaleLimits';

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const roundTo = (value, digits = 2) => {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
};

const withDefaultNumber = (value, fallback) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const withDefaultPositiveNumber = (value, fallback) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : fallback;
};

const scaleNumber = (value, ratio, fallback) => (
  roundTo(withDefaultNumber(value, fallback) * ratio, 2)
);

const scalePositiveNumber = (value, ratio, fallback) => (
  roundTo(withDefaultPositiveNumber(value, fallback) * ratio, 2)
);

export const applyMainScaleToVisualElements = (config, nextScale) => {
  const source = (config && typeof config === 'object') ? config : {};
  const currentScale = clamp(withDefaultNumber(source.scale, 1), MIN_SCALE, MAX_SCALE);
  const targetScale = clamp(withDefaultNumber(nextScale, currentScale), MIN_SCALE, MAX_SCALE);
  const roundedScale = Number(targetScale.toFixed(3));
  const ratio = targetScale / currentScale;

  const baseResult = {
    ...source,
    scale: roundedScale,
  };

  if (!Number.isFinite(ratio) || Math.abs(ratio - 1) < 1e-9) {
    return baseResult;
  }

  const scaled = {
    ...baseResult,
  };

  const sourcePrimitives = Array.isArray(source.shapePrimitives) ? source.shapePrimitives : null;
  if (!sourcePrimitives) return scaled;

  scaled.shapePrimitives = sourcePrimitives.map((shape) => {
    const nextShape = (shape && typeof shape === 'object') ? { ...shape } : {};
    const type = nextShape.type || 'rect';

    nextShape.x = scaleNumber(nextShape.x, ratio, 60);
    nextShape.y = scaleNumber(nextShape.y, ratio, 40);
    nextShape.strokeWidth = scalePositiveNumber(nextShape.strokeWidth, ratio, 3);
    nextShape.skewX = scaleNumber(nextShape.skewX, ratio, 0);
    if (nextShape.layerKind === 'text') {
      nextShape.textPaddingX = scaleNumber(nextShape.textPaddingX, ratio, 0);
    }

    if (type === 'circle') {
      const size = scalePositiveNumber(nextShape.width ?? nextShape.height, ratio, 90);
      nextShape.width = size;
      nextShape.height = size;
      nextShape.cornerRadius = 0;
      return nextShape;
    }

    if (type === 'line') {
      nextShape.width = scalePositiveNumber(nextShape.width, ratio, 220);
      nextShape.height = scalePositiveNumber(nextShape.height, ratio, 4);
      nextShape.cornerRadius = 0;
      return nextShape;
    }

    nextShape.width = scalePositiveNumber(nextShape.width, ratio, 220);
    nextShape.height = scalePositiveNumber(nextShape.height, ratio, 80);
    nextShape.cornerRadius = roundTo(
      Math.max(0, withDefaultNumber(nextShape.cornerRadius, type === 'roundedRect' ? 16 : 0) * ratio),
      2,
    );
    return nextShape;
  });

  return scaled;
};
